import { RefObject, useEffect, useRef } from 'react';

type AnyRef = RefObject<HTMLElement | null>;

/**
 * Calls `onOutside` when a mousedown lands outside any of the given refs.
 * Pass multiple refs if the "inside" region spans more than one node
 * (e.g. a trigger button + a portal-rendered menu).
 */
export const useClickOutside = (
  refs: AnyRef | AnyRef[],
  onOutside: () => void,
  enabled = true
) => {
  // Capture the latest callback so the listener stays fresh without
  // re-binding the document handler on every render.
  const onOutsideRef = useRef(onOutside);
  onOutsideRef.current = onOutside;

  useEffect(() => {
    if (!enabled) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      const refList = Array.isArray(refs) ? refs : [refs];
      const inside = refList.some((r) => r.current?.contains(target));
      if (!inside) onOutsideRef.current();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
    // refs are React refs (stable); onOutside is captured via the ref above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
};
